import { ModeBadge } from '../components/ModeBadge';
import { TenantProfileSwitcher } from '../components/TenantProfileSwitcher';
import { useWalkthroughStore } from '../state/walkthrough.store';
import { WALKTHROUGH_RELAUNCH_LABEL } from '../data/walkthrough-script';

/**
 * TopBar - fixed top bar for the AppShell (PROD-578 + PROD-568).
 *
 * Renders the Scrips logo, the product name, the pathway breadcrumb, the
 * Active and Draft status pills, the TenantProfileSwitcher, the Share
 * button, the Publish Pathway primary button, the ModeBadge, and the user
 * avatar. Per HC2 every color, spacing, and radius value is sourced from
 * Signal DS tokens via Tailwind (see src/styles/tokens.ts; tracked as G1
 * in SIGNAL-DS-CONTRIBUTIONS.md).
 *
 * Per HC5 the breadcrumb separator is the middle dot character (U+00B7,
 * `·`), never an em dash. Per ORIGINAL-PROTOTYPE-PROMPTS Prompt 4 the top
 * bar background is the `--sidebar` token (#0D1B4B).
 *
 * Phase 1 placeholders called out in PROD-578:
 *   - Active / Draft pills are static; PROD-576 wires real version state
 *     via the Pathway version state machine in Phase 2.
 *   - Share button opens an explanatory modal per ADR-PROTO-006 bearer
 *     token gate. The modal is intentionally a stub in Phase 1.
 *   - Publish Pathway button is disabled with a Phase 2 tooltip.
 *
 * The TenantProfileSwitcher (PROD-568) sits alongside the action group on
 * the right so the breadcrumb retains the focus on the left.
 */
export interface TopBarProps {
  pathwayBreadcrumb: {
    pathwayName: string;
    activeLabel: string;
    versionLabel: string;
  };
  userInitials: string;
  onShareClick?: () => void;
}

export function TopBar({ pathwayBreadcrumb, userInitials, onShareClick }: TopBarProps) {
  const openWalkthrough = useWalkthroughStore((s) => s.openManual);
  return (
    <header
      role="banner"
      data-walkthrough-anchor="welcome"
      className="flex h-12 items-center justify-between bg-sidebar px-gutter-lg text-text-inverse"
    >
      <div className="flex items-center gap-gutter-md">
        <a
          href="/"
          aria-label="Scrips home"
          className="flex items-center gap-2 rounded-signal-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sidebar focus:ring-sidebar-tab-active"
        >
          <img
            src="/favicon.svg"
            alt=""
            aria-hidden="true"
            className="h-6 w-6"
          />
          <span className="text-signal-base font-bold tracking-tight text-text-inverse">
            Scrips
          </span>
        </a>
        <span aria-hidden="true" className="text-signal-sm text-sidebar-muted">
          ·
        </span>
        <span className="text-signal-base font-semibold tracking-tight text-text-inverse">
          Pathway Studio
        </span>
        <span
          aria-hidden="true"
          className="text-signal-sm text-sidebar-muted"
        >
          ·
        </span>
        <nav
          aria-label="Pathway context"
          className="flex items-center gap-2 text-signal-sm text-sidebar-muted"
        >
          <span className="text-text-inverse">{pathwayBreadcrumb.pathwayName}</span>
          <span aria-hidden="true">·</span>
          <span
            className="inline-flex items-center gap-1 rounded-signal-pill bg-status-active-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-status-active-fg"
            aria-label={`Pathway status: ${pathwayBreadcrumb.activeLabel}`}
          >
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 animate-pulse rounded-signal-pill bg-status-active"
            />
            {pathwayBreadcrumb.activeLabel}
          </span>
          <span
            className="inline-flex items-center rounded-signal-pill bg-status-draft-bg px-gutter-sm py-0.5 text-signal-xs font-semibold text-status-draft-fg"
            aria-label={`Pathway version: ${pathwayBreadcrumb.versionLabel}`}
          >
            {pathwayBreadcrumb.versionLabel}
          </span>
        </nav>
      </div>

      <div className="flex items-center gap-gutter-md">
        <span data-walkthrough-anchor="tenant-switch">
          <TenantProfileSwitcher />
        </span>
        <button
          type="button"
          onClick={onShareClick}
          className="rounded-signal-md border border-sidebar-border px-gutter-md py-1 text-signal-sm font-medium text-text-inverse hover:bg-sidebar-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sidebar focus:ring-sidebar-tab-active"
        >
          Share
        </button>
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Phase 2"
          className="rounded-signal-md bg-sidebar-tab-active px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse opacity-60 cursor-not-allowed"
        >
          Publish Pathway
        </button>
        <button
          type="button"
          data-testid="walkthrough-relaunch-button"
          onClick={openWalkthrough}
          aria-label={WALKTHROUGH_RELAUNCH_LABEL}
          className="rounded-signal-md border border-sidebar-border px-gutter-md py-1 text-signal-sm font-medium text-text-inverse hover:bg-sidebar-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-sidebar focus:ring-sidebar-tab-active"
        >
          {WALKTHROUGH_RELAUNCH_LABEL}
        </button>
        <span data-walkthrough-anchor="mode-toggle">
          <ModeBadge />
        </span>
        <span
          aria-label={`Signed in as ${userInitials}`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-signal-pill bg-sidebar-hover text-signal-sm font-semibold text-text-inverse ring-1 ring-sidebar-border"
        >
          {userInitials}
        </span>
      </div>
    </header>
  );
}
